/**
 * Spatial Audio Types
 *
 * Type definitions for spatial audio processing
 */

/**
 * Spatial audio distance configuration
 *
 * DISTANCE-BASED GAIN SPECIFICATION (CUBIC FALLOFF):
 * ┌──────────────┬─────────┬────────────────────────────────┐
 * │ Distance     │ Gain %  │ Description                    │
 * ├──────────────┼─────────┼────────────────────────────────┤
 * │ 0.0 - 0.5m   │ 100%    │ Full volume (intimate)         │
 * │ 1.0m         │ ~90%    │ Very close - still loud        │
 * │ 2.0m         │ ~72%    │ Normal talking - NOTICEABLE    │
 * │ 3.0m         │ ~57%    │ Across table - CLEARLY QUIETER │
 * │ 5.0m         │ ~33%    │ Across room - MUCH QUIETER     │
 * │ 7.0m         │ ~17%    │ Far end of room - very faint   │
 * │ 10.0m        │ ~4%     │ Barely audible                 │
 * │ ≥15.0m       │ 0%      │ Silent (COMPLETE CUTOFF)       │
 * └──────────────┴─────────┴────────────────────────────────┘
 */
export interface SpatialDistanceConfig {
  refDistance?: number; // Full volume at this distance (meters)
  maxDistance?: number; // Silent beyond this distance (meters)
  rolloffFactor?: number; // Unused (kept for compatibility)
  unit?: "auto" | "meters" | "centimeters";
}

/**
 * Default spatial distance configuration
 * User requirement: 0.5m = 100%, 15m = 0% (audio OFF)
 */
export const DEFAULT_SPATIAL_CONFIG: Required<SpatialDistanceConfig> = {
  refDistance: 0.5, // Full volume at 0.5m or closer
  maxDistance: 15, // Silent at 15m (audio cutoff at 15.1m+)
  rolloffFactor: 1, // Unused - we use quadratic falloff
  unit: "auto",
};

/**
 * Denoiser options for noise suppression
 */
export interface DenoiserOptions {
  enabled?: boolean;
  threshold?: number;
  noiseFloor?: number;
  release?: number;
  attack?: number;
  holdMs?: number;
  maxReduction?: number;
  hissCut?: number;
  expansionRatio?: number;
  learnRate?: number;
  voiceBoost?: number;
  voiceSensitivity?: number;
  voiceEnhancement?: boolean;
  silenceFloor?: number;
  silenceHoldMs?: number;
  silenceReleaseMs?: number;
  speechBoost?: number;
  highBandGate?: number;
  highBandAttack?: number;
  highBandRelease?: number;
}

/**
 * Default denoiser configuration
 */
export const DEFAULT_DENOISER_OPTIONS: Required<DenoiserOptions> = {
  enabled: false,
  threshold: 0.002,
  noiseFloor: 0.0005,
  release: 0.4,
  attack: 0.1,
  holdMs: 200,
  maxReduction: 0.15,
  hissCut: 0.15,
  expansionRatio: 1.05,
  learnRate: 0.03,
  voiceBoost: 0.35,
  voiceSensitivity: 0.1,
  voiceEnhancement: false,
  silenceFloor: 0.0002,
  silenceHoldMs: 400,
  silenceReleaseMs: 200,
  speechBoost: 0.25,
  highBandGate: 0.15,
  highBandAttack: 0.08,
  highBandRelease: 0.03,
};

/**
 * Audio nodes for a participant
 */
export interface ParticipantAudioNodes {
  source: MediaStreamAudioSourceNode;
  panner: PannerNode;
  stereoPanner: StereoPannerNode;
  monoSplitter: ChannelSplitterNode;
  monoGainL: GainNode;
  monoGainR: GainNode;
  monoMerger: ChannelMergerNode;
  stereoUpmixer: ChannelMergerNode;
  analyser: AnalyserNode;
  gain: GainNode;
  proximityGain: GainNode;
  compressor: DynamicsCompressorNode;
  highpassFilter: BiquadFilterNode;
  lowpassFilter: BiquadFilterNode;
  voiceBandFilter: BiquadFilterNode;
  dynamicLowpass: BiquadFilterNode;
  denoiseNode?: AudioWorkletNode;
  stream: MediaStream;
}

/**
 * Listener state
 */
export interface ListenerState {
  position: {x: number; y: number; z: number};
  right: {x: number; z: number};
  initialized: boolean;
}
